import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X } from 'lucide-react'
import type { SlideData } from '../../types/presentation'

export interface SlideThumbnailsProps {
  slides: SlideData[]
  currentSlide: number
  onSlideSelect: (index: number) => void
  isOpen: boolean
  onClose: () => void
}

export const SlideThumbnails: React.FC<SlideThumbnailsProps> = ({
  slides,
  currentSlide,
  onSlideSelect,
  isOpen,
  onClose,
}) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            onClick={onClose}
          />

          {/* Panel */}
          <motion.div
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed left-0 top-0 bottom-0 w-80 bg-card shadow-2xl z-50 overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h2 className="text-lg font-semibold text-foreground">
                Slide Thumbnails
              </h2>
              <button
                onClick={onClose}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
                aria-label="Close thumbnails"
              >
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>

            {/* Thumbnails List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {slides.map((slide, index) => (
                <motion.button
                  key={slide.id}
                  onClick={() => onSlideSelect(index)}
                  className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                    index === currentSlide
                      ? 'border-primary bg-primary/10 shadow-md'
                      : 'border-border hover:border-primary/50 hover:shadow-sm'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {/* Thumbnail Preview with Title */}
                  <div className="relative aspect-video bg-gradient-to-br from-primary/10 to-secondary/10 rounded-md mb-3 overflow-hidden border border-border">
                    <div className="absolute inset-0 flex flex-col items-center justify-center p-4 text-center">
                      {/* Slide Title */}
                      {slide.title && (
                        <h3 className="text-sm font-semibold text-foreground mb-1 line-clamp-2">
                          {slide.title}
                        </h3>
                      )}

                      {/* Slide Subtitle */}
                      {slide.subtitle && (
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {slide.subtitle}
                        </p>
                      )}

                      {/* Content Preview if no title/subtitle */}
                      {!slide.title && !slide.subtitle && slide.content && (
                        <div className="text-xs text-muted-foreground line-clamp-4">
                          {typeof slide.content === 'string'
                            ? slide.content.substring(0, 150)
                            : 'Slide content'}
                        </div>
                      )}

                      {/* Fallback Icon */}
                      {!slide.title && !slide.subtitle && !slide.content && (
                        <div className="text-muted-foreground">
                          <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                      )}
                    </div>

                    {/* Current Slide Indicator Ring */}
                    {index === currentSlide && (
                      <div className="absolute inset-0 border-2 border-primary rounded-md pointer-events-none" />
                    )}

                    {/* Slide Number Badge */}
                    <div className={`absolute top-2 left-2 px-2 py-1 rounded text-xs font-bold ${
                      index === currentSlide
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-card/90 text-foreground'
                    }`}>
                      {index + 1}
                    </div>
                  </div>

                  {/* Slide Info */}
                  <div className="space-y-1">
                    <div className="text-sm font-medium text-foreground">
                      Slide {index + 1}
                    </div>
                    {slide.title && (
                      <div className="text-xs text-muted-foreground line-clamp-1">
                        {slide.title}
                      </div>
                    )}
                    {slide.layout && (
                      <div className="text-xs text-muted-foreground">
                        Layout: {slide.layout}
                      </div>
                    )}
                  </div>
                </motion.button>
              ))}
            </div>

            {/* Footer Info */}
            <div className="p-4 border-t border-border text-sm text-muted-foreground">
              {currentSlide + 1} of {slides.length} slides
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
